import { useState } from "react";
import { X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@components/ui/dialog";

interface AppNotificationModalProps {
  open: boolean;
  onClose: () => void;
  onDismiss: () => void;
  isDismissing: boolean;
  title: string;
  description?: string;
  children: React.ReactNode;
}

/**
 * Shared modal shell for app notifications.
 *
 * - "Ok" closes for this session (will show again next load)
 * - "Don't show again" checkbox + Ok permanently dismisses via API
 * - X button = same as Ok (closes without dismissing)
 */
export function AppNotificationModal({
  open,
  onClose,
  onDismiss,
  isDismissing,
  title,
  description,
  children,
}: AppNotificationModalProps) {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleOk = () => {
    if (dontShowAgain) {
      onDismiss();
    } else {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent
        className="sm:max-w-[600px] p-0 gap-0 [&>button]:hidden"
        data-testid="app-notification-modal"
      >
        <div className="px-12 pt-8 pb-8">
          <button
            type="button"
            onClick={onClose}
            className="absolute right-12 top-9 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            aria-label="Close"
          >
            <X className="h-[18px] w-[18px]" />
          </button>

          <DialogTitle className="text-lg font-semibold text-[#0f1113] mb-0.5">
            {title}
          </DialogTitle>
          {description && (
            <DialogDescription className="text-sm text-[#74767a] max-w-[460px]">
              {description}
            </DialogDescription>
          )}

          <div className="mt-6">{children}</div>

          <div className="flex items-center justify-between mt-8">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                className="h-4 w-4 rounded border-[#d0d0d0] text-[#0f1113] focus:ring-[#0f1113]"
              />
              <span className="text-sm text-[#96989b]">Don&apos;t show again</span>
            </label>
            <button
              type="button"
              onClick={handleOk}
              disabled={isDismissing}
              className="px-6 py-2.5 text-sm font-medium text-white bg-[#2563eb] rounded-lg hover:bg-[#1d4ed8] transition-colors disabled:opacity-50"
            >
              Ok
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
